/**
 * Cookie consent banner
 * Stores choice in localStorage and shows/hides the banner accordingly.
 * Other scripts can use getCookieConsent() or listen for 'ally:cookieConsent'.
 * Geolocation-aware: automatically accepts cookies and hides banner for US users.
 */

import { isUserFromUS, isUserFromUSCached } from '@lib/utils/geolocation.js';

const STORAGE_KEY = 'ally_cookie_consent';
const COOKIE_BANNER_HIDDEN_CLASS = 'cookie-banner--hidden';
const FOCUS_CLEANUP_SELECTOR = '.header__logo, .skip-content';

/** At most one document focusin listener for consent-announcement cleanup (avoids stacking on repeat calls). */
let activeConsentFocusInHandler = null;

/**
 * @returns {'accepted'|'rejected'|null}
 */
export function getCookieConsent() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'accepted' || v === 'rejected') {
      return v;
    }
  } catch {
    // Safari private mode, storage disabled, or quota exceeded
  }
  return null;
}

/**
 * @param {'accepted'|'rejected'} value
 */
function setCookieConsent(value) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Safari private mode, storage disabled, or quota exceeded
  }
  window.dispatchEvent(new CustomEvent('ally:cookieConsent', { detail: { consent: value } }));
}

const COOKIE_BANNER_VISIBLE_CLASS = 'cookie-banner-visible';
const BODY_COOKIE_BANNER_HEIGHT_VAR = '--cookie-banner-height';

/** Focusable elements inside the modal (links, buttons, [tabindex]:not(-1)) */
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function setBannerHeightVar(value) {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.style.setProperty(BODY_COOKIE_BANNER_HEIGHT_VAR, value);
  document.documentElement.style.setProperty(BODY_COOKIE_BANNER_HEIGHT_VAR, value);
}

function removeBannerHeightVar() {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.style.removeProperty(BODY_COOKIE_BANNER_HEIGHT_VAR);
  document.documentElement.style.removeProperty(BODY_COOKIE_BANNER_HEIGHT_VAR);
}

function setFooterReserveForBanner() {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.classList.add(COOKIE_BANNER_VISIBLE_CLASS);
  setBannerHeightVar('0');
}

function clearFooterReserveForBanner() {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.classList.remove(COOKIE_BANNER_VISIBLE_CLASS);
  removeBannerHeightVar();
}

function lockBodyScroll() {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
}

function unlockBodyScroll() {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
}

function hideBanner(banner) {
  if (!banner) {
    return;
  }
  unlockBodyScroll();
  clearFooterReserveForBanner();
  banner.classList.add(COOKIE_BANNER_HIDDEN_CLASS);
  banner.setAttribute('aria-hidden', 'true');
  banner.setAttribute('inert', '');
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  if (!container) {
    return [];
  }
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null && !el.hasAttribute('disabled')
  );
}

/**
 * Keeps Tab/Shift+Tab focus inside the modal.
 * @param {KeyboardEvent} e
 * @param {HTMLElement} banner
 */
function handleFocusTrapKeydown(e, banner) {
  if (e.key !== 'Tab') {
    return;
  }
  const focusables = getFocusableElements(banner);
  if (focusables.length === 0) {
    return;
  }
  const [first] = focusables;
  const last = focusables.at(-1);
  const current = document.activeElement;
  if (e.shiftKey) {
    if (current === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (current === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

/**
 * Move focus into the modal and trap it there. Call removeFocusTrap when closing.
 * Focus the dialog first so VoiceOver announces it (role + name), then move to the first
 * focusable so the user can interact and focus stays inside the modal.
 * @param {HTMLElement} banner
 */
function setupFocusTrap(banner) {
  if (!banner || !banner.classList.contains('cookie-banner--modal')) {
    return;
  }
  const focusables = getFocusableElements(banner);
  if (focusables.length === 0) {
    return;
  }
  banner._cookieBannerPreviousFocus = document.activeElement;
  const handler = (e) => handleFocusTrapKeydown(e, banner);
  banner._cookieBannerTrapHandler = handler;
  document.addEventListener('keydown', handler);
  // Focus the dialog first so VoiceOver announces "Cookie Information, dialog" (aria-labelledby).
  if (typeof banner.focus === 'function') {
    banner.focus({ preventScroll: true });
  }
  // Then move focus to the first focusable so the user can Tab and so focus stays in the modal.
  const [first] = focusables;
  if (first && typeof first.focus === 'function') {
    requestAnimationFrame(() => {
      first.focus({ preventScroll: true });
    });
  }
  // After 400ms, move focus back to the dialog so the Privacy Policy link loses its outline on page load.
  setTimeout(() => {
    if (
      document.activeElement &&
      banner.contains(document.activeElement) &&
      typeof banner.focus === 'function'
    ) {
      banner.focus({ preventScroll: true });
    }
  }, 400);
}

/**
 * Remove focus trap and optionally restore focus to the element that had it before the modal opened.
 * @param {HTMLElement} banner
 * @param {{ restoreFocus?: boolean }} [options] - If restoreFocus is false, only the trap is removed (used when focusing the announcement so the page title is not read instead).
 */
function removeFocusTrap(banner, options) {
  if (!banner) {
    return;
  }
  const handler = banner._cookieBannerTrapHandler;
  if (handler) {
    document.removeEventListener('keydown', handler);
    banner._cookieBannerTrapHandler = null;
  }
  const previous = banner._cookieBannerPreviousFocus;
  banner._cookieBannerPreviousFocus = null;
  const restoreFocus = options?.restoreFocus !== false;
  if (
    restoreFocus &&
    previous &&
    typeof previous.focus === 'function' &&
    document.contains(previous)
  ) {
    previous.focus({ preventScroll: true });
  }
}

/**
 * Shows the modal banner: moves it to start of body, clears hidden state, locks scroll, sets up focus trap.
 * @param {HTMLElement} banner
 */
function slideUpAndShow(banner) {
  if (!banner) {
    return;
  }
  if (banner.parentNode !== document.body || banner !== document.body.firstChild) {
    document.body.insertBefore(banner, document.body.firstChild);
  }
  // Drop preload hiding so stylesheet controls appearance; dialog is then exposed to AT like before.
  banner.style.removeProperty('opacity');
  banner.style.removeProperty('visibility');
  banner.style.removeProperty('pointer-events');
  banner.removeAttribute('inert');
  banner.removeAttribute('aria-hidden');
  banner.classList.remove(COOKIE_BANNER_HIDDEN_CLASS);
  banner.style.transform = '';
  lockBodyScroll();
  setFooterReserveForBanner();
  setupFocusTrap(banner);
}

/**
 * Hides the modal banner and runs the onHidden callback.
 * @param {HTMLElement} banner
 * @param {() => void} [onHidden] - Called after the banner is hidden (e.g. to remove focus trap).
 */
function slideDownAndHide(banner, onHidden) {
  if (!banner) {
    return;
  }
  setBannerHeightVar('0');
  hideBanner(banner);
  onHidden?.();
}

/**
 * Announces the consent result to screen readers (live region + focus-based cleanup). Callers focus
 * the announcement so it is read before page context (e.g. page title).
 * @param {HTMLElement} _banner - Reserved for future use.
 * @param {'accepted'|'rejected'} value
 */
function announceConsentResult(_banner, value) {
  const message =
    value === 'accepted' ? 'Cookies have been accepted.' : 'Cookies have been rejected.';
  const announcementEl = document.getElementById('cookie-consent-announcement');

  if (!announcementEl) {
    return;
  }

  // Replace any previous listener so double-clicks / re-announcement cannot stack handlers.
  if (activeConsentFocusInHandler) {
    document.removeEventListener('focusin', activeConsentFocusInHandler);
    activeConsentFocusInHandler = null;
  }

  announcementEl.removeAttribute('aria-hidden');
  announcementEl.textContent = message;

  function cleanup() {
    announcementEl.setAttribute('aria-hidden', 'true');
    announcementEl.textContent = '';
    document.removeEventListener('focusin', handleFocusIn);
    if (activeConsentFocusInHandler === handleFocusIn) {
      activeConsentFocusInHandler = null;
    }
  }

  function handleFocusIn(event) {
    const { target } = event;
    if (target && typeof target.closest === 'function' && target.closest(FOCUS_CLEANUP_SELECTOR)) {
      cleanup();
    }
  }

  document.addEventListener('focusin', handleFocusIn);
  activeConsentFocusInHandler = handleFocusIn;
}

function handleConsentClick(banner, value) {
  announceConsentResult(banner, value);
  setCookieConsent(value);
  // Focus the announcement first so the screen reader reads "Cookies have been accepted/rejected" before the dialog closes.
  const announcementEl = document.getElementById('cookie-consent-announcement');
  if (announcementEl && typeof announcementEl.focus === 'function') {
    announcementEl.focus({ preventScroll: true });
  }
  slideDownAndHide(banner, () => {
    removeFocusTrap(banner, { restoreFocus: false });
  });
}

export async function initCookieBanner() {
  const banner = document.querySelector('[data-cookie-banner]');
  if (!banner) {
    return;
  }

  // Initially hide the banner completely to prevent flash
  banner.style.display = 'none';
  banner.setAttribute('aria-hidden', 'true');
  banner.setAttribute('inert', '');

  // Check if already consented first (fastest check)
  if (getCookieConsent() !== null) {
    banner.remove();
    return;
  }

  // Fast synchronous check for cached US user data
  const cachedUSCheck = isUserFromUSCached();
  if (cachedUSCheck === true) {
    // For US users: automatically accept cookies and hide the banner
    setCookieConsent('accepted');
    banner.remove();
    return;
  }

  let bannerShown = false;
  let timeoutId;

  // If cached data shows non-US user, show banner immediately
  if (cachedUSCheck === false) {
    showBanner();
    return;
  }

  // Function to show the banner
  function showBanner() {
    if (bannerShown) {
      return;
    }
    bannerShown = true;

    // Clear timeout if banner is being shown manually
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    // Remove the initial hiding and show the banner properly
    banner.style.display = '';

    const acceptBtn = banner.querySelector('[data-cookie-consent="accept"]');
    const rejectBtn = banner.querySelector('[data-cookie-consent="reject"]');

    slideUpAndShow(banner);

    acceptBtn?.addEventListener('click', () => {
      handleConsentClick(banner, 'accepted');
    });

    rejectBtn?.addEventListener('click', () => {
      handleConsentClick(banner, 'rejected');
    });
  }

  // Function to hide banner if US user is detected after it's shown
  function hideBannerForUSUser() {
    if (bannerShown && banner && document.contains(banner)) {
      slideDownAndHide(banner, () => {
        removeFocusTrap(banner, { restoreFocus: false });
        banner.remove();
      });
    }
  }

  // Set timeout to show banner after 3 seconds if no response yet
  timeoutId = setTimeout(() => {
    showBanner();
  }, 3000);

  // Continue geolocation check in background
  try {
    const isUSUser = await isUserFromUS();

    if (isUSUser) {
      // Clear timeout since we got a result
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // For US users: automatically accept cookies and hide the banner
      if (getCookieConsent() === null) {
        setCookieConsent('accepted');
      }

      // If banner was already shown, hide it; otherwise just remove it
      if (bannerShown) {
        hideBannerForUSUser();
      } else {
        banner.remove();
      }
    } else {
      showBanner();
    }
  } catch (error) {
    console.warn('Error checking user location, showing cookie banner as fallback:', error);
    // On error, show the banner (safer default)
    showBanner();
  }
}
